'use client';

import Link from 'next/link';
import { useState } from 'react';

const COLORS = {
  primary: '#1A237E',
  accent: '#1B5E20',
  highlight: '#B8860B',
  background: '#E3F2FD',
  textPrimary: '#1A237E',
  textSecondary: '#455A64',
};

export default function HomePage() {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  return (
    <div
      className="min-h-screen p-8 font-serif"
      style={{
        background: `linear-gradient(135deg, ${COLORS.background} 0%, #ffffff 50%, #E1F5FE 100%)`,
      }}
    >
      {/* Header Section */}
      <div className="max-w-6xl mx-auto mb-16">
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold mb-4" style={{ color: COLORS.primary }}>
            India's Basmati Rice Trade
          </h1>
          <p className="text-xl" style={{ color: COLORS.textSecondary }}>
            Export Analytics & Strategic Market Intelligence
          </p>
          <div className="h-1 w-40 mx-auto mt-6 rounded-full" style={{ background: `linear-gradient(90deg, ${COLORS.primary} 0%, ${COLORS.highlight} 100%)` }} />
        </div>

        {/* Main Dashboard Card */}
        <div className="mb-12">
          <Link href="/dashboard">
            <div
              className="rounded-3xl backdrop-blur-md bg-white/95 shadow-2xl border border-white/20 p-8 hover:shadow-3xl transition-all duration-300 cursor-pointer group"
              style={{
                background: 'rgba(255, 255, 255, 0.97)',
                border: `2px solid ${COLORS.primary}`,
                transform: hoveredCard === 'main' ? 'scale(1.02) translateY(-8px)' : 'scale(1)',
              }}
              onMouseEnter={() => setHoveredCard('main')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="grid grid-cols-3 gap-8 mb-8">
                {/* Stat Card 1 */}
                <div className="text-center p-6 rounded-2xl" style={{ background: '#F5FFFA', border: `2px solid ${COLORS.accent}` }}>
                  <div className="text-4xl mb-3">📊</div>
                  <p className="text-sm font-semibold mb-2" style={{ color: COLORS.textSecondary }}>
                    Total Export
                  </p>
                  <p className="text-3xl font-bold" style={{ color: COLORS.primary }}>
                    114.8M
                  </p>
                  <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                    Metric Tonnes
                  </p>
                </div>

                {/* Stat Card 2 */}
                <div className="text-center p-6 rounded-2xl" style={{ background: '#F5FFFA', border: `2px solid ${COLORS.accent}` }}>
                  <div className="text-4xl mb-3">🍚</div>
                  <p className="text-sm font-semibold mb-2" style={{ color: COLORS.textSecondary }}>
                    Basmati Export
                  </p>
                  <p className="text-3xl font-bold" style={{ color: COLORS.primary }}>
                    36.0M
                  </p>
                  <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                    31.4% of Total
                  </p>
                </div>

                {/* Stat Card 3 */}
                <div className="text-center p-6 rounded-2xl" style={{ background: '#F5FFFA', border: `2px solid ${COLORS.accent}` }}>
                  <div className="text-4xl mb-3">📈</div>
                  <p className="text-sm font-semibold mb-2" style={{ color: COLORS.textSecondary }}>
                    Growth Rate
                  </p>
                  <p className="text-3xl font-bold" style={{ color: COLORS.highlight }}>
                    4.2%
                  </p>
                  <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                    10-Year CAGR
                  </p>
                </div>
              </div>

              {/* Chart Visualization */}
              <div className="mb-8 p-6 rounded-2xl" style={{ background: '#F0F8FF' }}>
                <p className="text-sm font-bold mb-4" style={{ color: COLORS.primary }}>
                  Top Export Markets
                </p>
                <div className="space-y-3">
                  {[
                    { country: 'Iran', value: 20.4 },
                    { country: 'Saudi Arabia', value: 17.9 },
                    { country: 'Iraq', value: 13.0 },
                    { country: 'UAE', value: 10.5 },
                  ].map((item) => (
                    <div key={item.country} className="flex items-center gap-3">
                      <span className="text-sm font-semibold w-24" style={{ color: COLORS.textSecondary }}>
                        {item.country}
                      </span>
                      <div className="flex-1 h-8 rounded-lg overflow-hidden" style={{ background: '#E3F2FD' }}>
                        <div
                          className="h-full rounded-lg transition-all duration-500"
                          style={{
                            width: `${item.value * 4}%`,
                            background: `linear-gradient(90deg, ${COLORS.primary} 0%, ${COLORS.accent} 100%)`,
                          }}
                        />
                      </div>
                      <span className="text-sm font-bold w-12 text-right" style={{ color: COLORS.primary }}>
                        {item.value}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2" style={{ color: COLORS.primary }}>
                    Full Analytics Dashboard
                  </h2>
                  <p style={{ color: COLORS.textSecondary }}>
                    Explore detailed market analysis, trends, and strategic insights
                  </p>
                </div>
                <div
                  className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 group-hover:scale-110"
                  style={{
                    background: COLORS.primary,
                    color: 'white',
                  }}
                >
                  Explore →
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-3 gap-6 mb-12">
          {/* Feature 1 */}
          <div
            className="p-6 rounded-2xl backdrop-blur-md bg-white/90 border border-white/20 hover:shadow-xl transition-all duration-300 cursor-pointer"
            style={{
              transform: hoveredCard === 'feature1' ? 'translateY(-8px)' : 'translateY(0)',
            }}
            onMouseEnter={() => setHoveredCard('feature1')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-lg font-bold mb-2" style={{ color: COLORS.primary }}>
              Market Analysis
            </h3>
            <p style={{ color: COLORS.textSecondary }}>
              11-year export trends, market concentration, and geopolitical risk assessment
            </p>
          </div>

          {/* Feature 2 */}
          <div
            className="p-6 rounded-2xl backdrop-blur-md bg-white/90 border border-white/20 hover:shadow-xl transition-all duration-300 cursor-pointer"
            style={{
              transform: hoveredCard === 'feature2' ? 'translateY(-8px)' : 'translateY(0)',
            }}
            onMouseEnter={() => setHoveredCard('feature2')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="text-4xl mb-4">🌍</div>
            <h3 className="text-lg font-bold mb-2" style={{ color: COLORS.primary }}>
              Strategic Expansion
            </h3>
            <p style={{ color: COLORS.textSecondary }}>
              Vietnam, Indonesia, Philippines expansion targets with growth projections
            </p>
          </div>

          {/* Feature 3 */}
          <div
            className="p-6 rounded-2xl backdrop-blur-md bg-white/90 border border-white/20 hover:shadow-xl transition-all duration-300 cursor-pointer"
            style={{
              transform: hoveredCard === 'feature3' ? 'translateY(-8px)' : 'translateY(0)',
            }}
            onMouseEnter={() => setHoveredCard('feature3')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="text-4xl mb-4">📈</div>
            <h3 className="text-lg font-bold mb-2" style={{ color: COLORS.primary }}>
              Growth Insights
            </h3>
            <p style={{ color: COLORS.textSecondary }}>
              CAGR analysis, market opportunities, and actionable recommendations
            </p>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <Link href="/dashboard">
            <button
              className="px-12 py-4 rounded-2xl font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
              style={{
                background: COLORS.primary,
                color: 'white',
              }}
            >
              🚀 Launch Dashboard
            </button>
          </Link>
          <p className="mt-6" style={{ color: COLORS.textSecondary }}>
            Interactive charts • Strategic insights • Market intelligence
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-8 mt-16 border-t-2" style={{ borderColor: '#E0E0E0' }}>
        <p style={{ color: COLORS.textSecondary }}>
          India's Basmati Rice Trade Analytics Platform
        </p>
      </div>
    </div>
  );
}
